<template>
  <div class="multi-date-picker" data-cy="multi-date-picker">
    <div class="date-display" data-cy="date-display" @click="openPopup">
      <p class="strong">{{ t('matrix.requested_date') }}</p>
      <div class="bottom-row">
        <p>{{ formattedSelection }}</p>
        <BackstageIcon :name="Icons.CALENDAR" class="calendar-icon" />
      </div>
    </div>

    <div v-if="showCalendarPopup" class="modal-overlay" data-cy="modal-overlay" @click="closePopup">
      <div class="calendar-modal" data-cy="calendar-modal" @click.stop>
        <button class="close-btn" data-cy="close-popup" @click="closePopup">
          <BackstageIcon :name="Icons.CLOSE" />
        </button>
        <div class="modal-content">
          <div class="modal-header">
            <h3 class="title">{{ t('calendarPopup.select_dates') }}</h3>
          </div>

          <VueDatePicker
            six-weeks="center"
            v-model="selectedDates"
            inline
            multi-dates
            :disabled-dates="disabledDates"
            :enable-time-picker="false"
            :locale="userLocale"
            :format="formatDateNoUTC"
            :dark="isDark"
            week-start="1"
            auto-apply
            data-cy="date-picker">
            <template #month-year="{ month, year, handleMonthYearChange }">
              <div class="calendar-title">
                <div class="month-nav">
                  <button class="nav-arrow" data-cy="month-left-arrow" @click="handleMonthYearChange(false)">
                    <BackstageIcon :name="Icons.CHEVRON_LEFT" class="left-arrow" />
                  </button>
                  <span class="month-title">{{ t(`global.months.${monthNames[month]}`) }} {{ year }}</span>
                  <button class="nav-arrow" data-cy="month-right-arrow" @click="handleMonthYearChange(true)">
                    <BackstageIcon :name="Icons.CHEVRON_LEFT" class="right-arrow" />
                  </button>
                </div>
                <span class="selected-count">{{ selectedCountText }}</span>
              </div>
            </template>

            <template #day="{ day, date }">
              <div class="day-content-wrapper">
                <span>{{ day }}</span>
                <span
                  v-if="isLaunchDate(date)"
                  class="launch-date-label"
                  :class="{ 'selected-label': isDateSelected(date) }"
                  >{{ t('viewOrder.launch_date') }}</span
                >
              </div>
            </template>
          </VueDatePicker>
        </div>
        <div class="modal-footer">
          <div class="modal-footer-info">
            <p v-if="hasZeroDateSelected" class="modal-footer-info-text">
              {{ t('calendarPopup.all_unselected_tip') }}
            </p>
            <p v-else-if="hasOneInitialDate" class="modal-footer-info-text">
              {{ t('calendarPopup.one_initial_date_tip') }}
            </p>
            <p v-else-if="hasMoreThanOneInitialDate" class="modal-footer-info-text">
              {{ t('calendarPopup.more_than_one_initial_date_tip') }}
            </p>
            <BackstageCheckbox
              v-if="hasOneInitialDate"
              v-model="copyQuantity"
              data-cy="copy-quantity-checkbox"
              class="modal-footer-info-copy-quantity">
              {{ t('calendarPopup.copy_quantities') }}
            </BackstageCheckbox>
          </div>
          <div class="modal-footer-actions">
            <BackstageButton
              class="btn-secondary"
              full-width
              :variant="ButtonVariant.SECONDARY"
              data-cy="deselect-all-button-cnt"
              data-cy-button="deselect-all-button"
              @click="deselectAll">
              {{ t('global.deselect_all_button') }}
            </BackstageButton>
            <BackstageButton
              class="btn-primary"
              full-width
              :icon-right="Icons.ARROW_RIGHT"
              data-cy="save-button-cnt"
              data-cy-button="save-button"
              @click="saveSelection"
              >{{ t('global.save_button') }}
            </BackstageButton>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import BackstageIcon from '@/components/common/BackstageIcon.vue';
import { useFormat } from '@/composables/format';
import { useLocale } from '@/composables/locale';
import { BackstageTheme } from '@/models/backstage-theme.enum';
import { ButtonVariant } from '@/models/button-variant.enum';
import { Icons } from '@/models/icons.enum';
import { useUserStore } from '@/stores/user';
import VueDatePicker from '@vuepic/vue-datepicker';
import '@vuepic/vue-datepicker/dist/main.css';
import { storeToRefs } from 'pinia';
import { computed, ref, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import { monthNames } from '../../constants/global.constants';
import { isDateBefore, isSameDate, today, toISOString } from '../../utils/temporalUtils';
import BackstageButton from '../common/BackstageButton.vue';
import BackstageCheckbox from '../common/BackstageCheckbox.vue';

const props = defineProps<{
  launchDate: string | Date;
  initialDates: Date[];
  preOrderEarliestRequestedDate?: string;
}>();

export type UpdateDatesProps = {
  dates: Date[];
  copyQuantity: boolean;
};

const { t } = useI18n();
const { userLocale } = useLocale();
const { formatDateNoUTC } = useFormat();
const { userProfile } = storeToRefs(useUserStore());

const emit = defineEmits<(e: 'update:dates', { dates, copyQuantity }: UpdateDatesProps) => void>();

const showCalendarPopup = ref(false);
const selectedDates = ref<Date[]>([]);
const copyQuantity = ref(false);

watch(selectedDates, (newDates: Date[]) => {
  if (newDates.length === 0) {
    copyQuantity.value = false;
  }
});

const isDark = computed(() => {
  return userProfile.value?.user?.theme === BackstageTheme.dark;
});

const hasOneInitialDate = computed(() => {
  return props.initialDates.length === 1;
});

const hasMoreThanOneInitialDate = computed(() => {
  return props.initialDates.length > 1;
});

const hasZeroDateSelected = computed(() => {
  return selectedDates.value.length === 0;
});

const selectedCountText = computed(() => {
  const numberOfDaysSelected = selectedDates.value.length;

  return t('calendarPopup.number_of_days_selected', { numberOfDaysSelected: numberOfDaysSelected });
});

const formattedSelection = computed(() => {
  if (!props.preOrderEarliestRequestedDate) {
    return toISOString(today());
  }

  return toISOString(props.preOrderEarliestRequestedDate);
});

const disabledDates = (date: Date) => {
  if (!props.preOrderEarliestRequestedDate) {
    return false; // no dates disabled
  }

  return isDateBefore(date, props.preOrderEarliestRequestedDate);
};

const openPopup = () => {
  selectedDates.value = [...props.initialDates];
  showCalendarPopup.value = true;
};

const closePopup = () => {
  showCalendarPopup.value = false;
};

const saveSelection = () => {
  emit('update:dates', { dates: selectedDates.value, copyQuantity: copyQuantity.value });
  closePopup();
};

const deselectAll = () => {
  selectedDates.value = [];
};

const isLaunchDate = (date: Date) => {
  return isSameDate(date, props.launchDate);
};

const isDateSelected = (date: Date) => {
  return selectedDates.value.some((d) => isSameDate(date, d));
};
</script>

<style lang="scss" scoped>
.date-display {
  display: flex;
  flex-direction: column;
  cursor: pointer;

  p.strong {
    margin: 0 0 4px 0;
    font-size: 14px;
    color: var(--shade-4);
  }

  .bottom-row {
    display: flex;
    align-items: center;
    gap: 8px;
  }

  .bottom-row p {
    margin: 0;
    font-size: 12px;
  }

  .bottom-row .calendar-icon {
    width: 20px;
    height: 20px;
    background-color: var(--primary-text-color);
  }
}

.calendar-modal {
  position: relative;
  background: var(--shade-1);
  border-radius: 12px;
  width: 100%;
  max-width: 720px;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  box-shadow: 0 8px 24px rgba(0, 0, 0, 0.15);

  @media (max-width: 1512px) {
    transform: scale(0.9);
  }
}

.modal-header {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  margin: 0;
}

.title {
  font-size: 20px;
  font-weight: 600;
  margin: 0;
  width: 100%;
}

.modal-content {
  display: flex;
  flex-direction: column;
  align-items: center;
  width: 100%;
  height: 100%;
  margin-top: 3rem;
}

.close-btn {
  position: absolute;
  top: 20px;
  right: 18px;
  background: none;
  border: none;
  font-size: 24px;
  cursor: pointer;
  line-height: 1;
  transition: color 0.2s ease;

  &:hover {
    color: var(--secondary-bg-color);
  }
}

.modal-footer {
  width: 65%;
  margin-bottom: 2rem;
}

.modal-footer-actions {
  display: flex;
  gap: 20px;
  margin-top: 20px;

  .btn-secondary,
  .btn-primary {
    flex: 1;
  }
}

.modal-overlay {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.3);
  display: flex;
  justify-content: center;
  align-items: center;
  z-index: 9999;
}

.calendar-modal :deep(.dp__instance_calendar) {
  max-width: 505px;
}

.calendar-modal :deep(.dp__main) {
  display: flex;
  justify-content: center;
  background: var(--shade-1);
  border: none;
  font-family: sans-serif;
  width: 100%;
  max-width: 420px;
  margin: 0 auto;
  padding-top: 0;
}

.calendar-modal :deep(.dp__today) {
  border: none;
  font-family: 'On Semi Mono', Helvetica, sans-serif;
  font-style: normal;
  font-weight: 700;
  color: var(--vivid-blue-color);
}

.calendar-modal :deep(.dp__month_year_row) {
  margin-bottom: 6px !important;
}

.calendar-modal :deep(.dp__calendar) {
  margin: 0 auto;
  padding: 0;
}
.calendar-modal :deep(.dp__menu) {
  background: var(--shade-1);
  border: none;
}
.calendar-modal :deep(.dp__calendar_header) {
  font-family: 'On Semi Mono', Helvetica, sans-serif;
  font-weight: 400;
  color: var(--shade-4);
}
.calendar-modal :deep(.dp__calendar_header_item) {
  padding: 0.5rem 0;
}
.calendar-modal :deep(.dp__calendar_item) {
  background: var(--primary-bg-color);
  border: 1px solid var(--shade-1);
  border-radius: 0;
  height: 70px;
  width: 70px;
}

.calendar-modal :deep(.dp__cell_inner) {
  width: 100%;
  height: 100%;
  display: flex;
  flex-direction: column;
  justify-content: flex-start;
  align-items: flex-start;
  box-sizing: border-box;
  border-radius: 0;
}

.calendar-modal :deep(.dp__active_date) {
  background-color: var(--secondary-bg-color);
  color: var(--primary-bg-color);
  width: 100%;
  height: 100%;
  display: flex;
  justify-content: center;
  align-items: center;
}

.calendar-modal :deep(.dp__cell_offset.dp__active_date) {
  background-color: transparent !important;
}

.calendar-modal :deep(.dp__calendar_item:has(.dp__cell_offset)) {
  background: transparent;
  border: none;
  pointer-events: none;
}

.calendar-modal :deep(.dp__calendar_item:has(.dp__cell_disabled)) {
  background: var(--shade-2);
  border: 1px solid var(--shade-1);
  pointer-events: none;
}

.calendar-modal :deep(.dp__cell_offset) {
  color: transparent;
}

.calendar-modal :deep(.dp__cell_disabled) {
  color: var(--shade-4) !important;
  opacity: 1 !important;
}

.calendar-modal :deep(.dp__cell_disabled .day-content-wrapper) {
  opacity: 0.4;
}

.calendar-modal :deep(.dp__main .dp__month_year_row) {
  --dp-month-year-row-height: 58px;
  background-color: transparent;
}

.calendar-header-content {
  display: flex;
  justify-content: space-between;
  align-items: center;
  width: 100%;
}

.month-nav {
  display: flex;
  align-items: center;
  font-weight: 600;
  font-size: 16px;
  min-width: 200px;
}

.month-title {
  margin: 0 8px;
  font-weight: 500;
  flex: 1;
}

.modal-footer-info {
  .modal-footer-info-text {
    margin: 0;
    font-size: 14px;
    line-height: 1.5;
    color: var(--scarlet-red-color);
    text-align: left;
    margin-bottom: 20px;
  }
  margin-bottom: 60px;
}

.nav-arrow {
  background: none;
  border: none;
  cursor: pointer;

  .left-arrow,
  .right-arrow {
    display: block;
    width: 18px;
    height: 18px;
  }

  .right-arrow {
    transform: rotate(180deg);
  }
}

.calendar-title {
  display: flex;
  justify-content: space-between;
  width: 100%;
  align-items: center;
  font-weight: 600;

  .selected-count {
    font-size: 14px;
    color: var(--shade-4);
    font-weight: 400;
  }
}

.day-content-wrapper {
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  justify-content: flex-start;
  width: 100%;
  height: 100%;
  padding: 1px;
  box-sizing: border-box;
}

.launch-date-label {
  bottom: 2px;
  font-size: 14px;
  color: var(--primary-text-color);
  margin-top: 4px;
  line-height: 1;
  text-align: left;
  display: block;
  width: 100%;
}

.launch-date-label.selected-label {
  color: var(--secondary-text-color);
}
</style>
